import { useEffect, useState } from "react";
import { Container, Paper, Button, Alert, Loader, Text, Space, List, ThemeIcon } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const ThankYouPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);

  // Get all params
  const email = query.get("email") ?? "";
  // Clickworker params
  const clickworkerUser = query.get("user") || "";
  const clickworkerUserId = query.get("user_id") || "";
  const clickworkerTaskId = query.get("task_id") || "";
  const clickworkerJobId = query.get("job_id") || "";
  // Prolific params (legacy)
  const prolificPid = query.get("PROLIFIC_PID") || "";

  // Use clickworker_user_id or prolific_pid as the effective user ID
  const effectiveUserId = clickworkerUserId || prolificPid || email;

  const apiUrl = process.env.REACT_APP_API_URL_2 || "https://api.dbackup.cloud";

  // State
  const [loading, setLoading] = useState(true);
  const [takeoutStatus, setTakeoutStatus] = useState({
    takeout_requested: false,
    oauth_completed: false,
    folder_scheduled: false
  });
  const [error, setError] = useState("");

  // Check if all steps are complete
  const isComplete = takeoutStatus.takeout_requested &&
                     takeoutStatus.oauth_completed &&
                     takeoutStatus.folder_scheduled;

  // Check takeout status on page load
  useEffect(() => {
    const checkTakeoutStatus = async () => {
      if (!effectiveUserId) {
        setLoading(false);
        setError("No user ID found. Please complete the survey first.");
        return;
      }

      try {
        // Check takeout tracking status from our new tracker
        const response = await axios.get(`${apiUrl}/takeout/status/${effectiveUserId}`);
        const data = response.data;

        setTakeoutStatus({
          takeout_requested: data.takeout_requested || false,
          oauth_completed: data.oauth_completed || false,
          folder_scheduled: data.folder_scheduled || false
        });

        console.log("Takeout status:", {
          takeout_requested: data.takeout_requested,
          oauth_completed: data.oauth_completed,
          folder_scheduled: data.folder_scheduled
        });
      } catch (err) {
        console.error("Failed to check takeout status:", err);
        // If 404, takeout tracking doesn't exist - might be coming from old flow
        if (err.response?.status === 404) {
          setError("Takeout donation not found. Please complete the Google Takeout donation first.");
        } else {
          setError("Failed to verify takeout status. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    checkTakeoutStatus();
  }, [effectiveUserId, apiUrl]);

  // Build query string for redirect - preserves all tracking params
  const buildQueryString = () => {
    const params = new URLSearchParams();
    if (email) params.set("email", email);
    if (clickworkerUser) params.set("user", clickworkerUser);
    if (clickworkerUserId) params.set("user_id", clickworkerUserId);
    if (clickworkerTaskId) params.set("task_id", clickworkerTaskId);
    if (clickworkerJobId) params.set("job_id", clickworkerJobId);
    if (prolificPid) params.set("PROLIFIC_PID", prolificPid);
    return params.toString();
  };

  const handleContinueToChatGPT = () => {
    const queryString = buildQueryString();
    navigate(`/chatgpt-steps?${queryString}`);
  };

  const handleGoBackToDonate = () => {
    const queryString = buildQueryString();
    navigate(`/donate?${queryString}`);
  };

  // Loading state
  if (loading) {
    return (
      <Container size="md" mt="xl">
        <Paper padding="lg" withBorder style={{ textAlign: "center" }}>
          <Loader size="lg" />
          <Text mt="md">Verifying your Google Takeout donation...</Text>
        </Paper>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container size="md" mt="xl">
        <Paper padding="lg" withBorder>
          <Alert title="Verification Issue" color="red">
            {error}
          </Alert>
          <Space h="md" />
          <Text align="center">
            Please go back and complete the Google Takeout donation first.
          </Text>
          <Space h="md" />
          <Button fullWidth onClick={handleGoBackToDonate}>
            Go to Donation Page
          </Button>
        </Paper>
      </Container>
    );
  }

  // Step status icon helper
  const StepIcon = ({ completed }) => (
    <ThemeIcon color={completed ? "green" : "red"} size={24} radius="xl">
      {completed ? <IconCheck size={16} /> : <IconX size={16} />}
    </ThemeIcon>
  );

  return (
    <Container size="md" mt="xl">
      <Paper padding="lg" withBorder>
        <h1 style={{ textAlign: "center" }}>
          {isComplete ? "Google Takeout Donation Complete!" : "Google Takeout Status"}
        </h1>

        {isComplete ? (
          <>
            <Alert title="Step 1 Complete" color="green" mb="md">
              Your Google Takeout data has been successfully scheduled for backup.
            </Alert>

            <Text align="center" size="lg" mb="md">
              Now please complete Step 2: ChatGPT Data Donation
            </Text>

            <Text align="center" color="dimmed" size="sm" mb="lg">
              You must complete both donations to receive your completion code.
            </Text>

            <Button
              fullWidth
              size="lg"
              color="blue"
              onClick={handleContinueToChatGPT}
            >
              Continue to ChatGPT Data Donation
            </Button>
          </>
        ) : (
          <>
            <Alert title="Takeout Not Complete" color="yellow" mb="md">
              Your Google Takeout donation has not been fully completed yet. Please complete all steps below.
            </Alert>

            <Text align="center" mb="md" weight={600}>
              Step Status:
            </Text>

            <List spacing="md" mb="lg" center>
              <List.Item icon={<StepIcon completed={takeoutStatus.takeout_requested} />}>
                <Text color={takeoutStatus.takeout_requested ? "green" : "red"}>
                  {takeoutStatus.takeout_requested ? "Requested Google Takeout data" : "Google Takeout data NOT requested - Click 'Google Takeout' button on donation page"}
                </Text>
              </List.Item>
              <List.Item icon={<StepIcon completed={takeoutStatus.oauth_completed} />}>
                <Text color={takeoutStatus.oauth_completed ? "green" : "red"}>
                  {takeoutStatus.oauth_completed ? "Google authorization completed" : "Google authorization NOT completed - Complete the OAuth flow on dBackup"}
                </Text>
              </List.Item>
              <List.Item icon={<StepIcon completed={takeoutStatus.folder_scheduled} />}>
                <Text color={takeoutStatus.folder_scheduled ? "green" : "red"}>
                  {takeoutStatus.folder_scheduled ? "Backup scheduled on dBackup" : "Backup NOT scheduled - Confirm data donation on dBackup"}
                </Text>
              </List.Item>
            </List>

            <Button fullWidth size="lg" onClick={handleGoBackToDonate}>
              Go Back to Complete Takeout
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ThankYouPage;
